import { X } from 'lucide-react';
import type * as React from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonProps } from './button';
import { cn } from './lib/cn';
import { Spinner } from './spinner';

/**
 * Botao do rodape descrito como dado, nao como componente - assim toda modal
 * do sistema tem o mesmo desenho de acao.
 */
export interface ModalAction {
  label: string;
  /** Icone a esquerda do rotulo - deixa a acao reconhecivel antes da leitura. */
  icon?: React.ReactNode;
  onClick?: () => void;
  variant?: ButtonProps['variant'];
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
  /** Submete o formulario com este id, mesmo estando fora dele. */
  formId?: string;
}

/**
 * `auto` acompanha o conteudo, `full` ocupa a tela e um numero e a largura
 * em pixels.
 */
export type ModalSize = 'small' | 'medium' | 'large' | 'full' | 'auto' | number;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: ModalSize;
  /** Cabecalho discreto: usado quando o conteudo e que importa (previa de PDF). */
  compactHeader?: boolean;
  /** Corpo sem recuo: para conteudo que ja tem moldura propria (um PDF). */
  flushBody?: boolean;
  /** Estilo do corpo - usado para dar fundo proprio a modais de cartoes. */
  bodyClassName?: string;
  actions?: ModalAction[];
  children: React.ReactNode;
  className?: string;
}

const NAMED_WIDTH: Record<Exclude<ModalSize, number>, string> = {
  small: 'sm:max-w-sm',
  medium: 'sm:max-w-2xl',
  large: 'sm:max-w-5xl',
  // Cheia e cheia mesmo: sem margem e sem canto arredondado. A altura vem do
  // pai (`inset-0` + `100dvh`), nunca de `100vh`: no iPhone a barra do
  // navegador entra na conta e o rodape da modal fica abaixo da tela.
  full: 'h-full w-screen max-w-none rounded-none sm:rounded-none',
  auto: 'sm:w-auto sm:max-w-[90vw]',
};

/**
 * Modal do sistema: titulo, corpo rolavel e rodape com as acoes.
 * Fecha no Esc, no clique fora e no X.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'medium',
  compactHeader = false,
  flushBody = false,
  bodyClassName,
  actions = [],
  children,
  className,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    // Trava o scroll do fundo enquanto o modal esta aberto.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const isPixelWidth = typeof size === 'number';
  const isFull = size === 'full';

  /**
   * Sempre em portal no `body`: um ancestral com `backdrop-blur` (a barra do
   * topo, por exemplo) vira containing block e prenderia o `fixed` da modal
   * dentro da altura dele.
   */
  return createPortal(
    <div
      className={cn(
        // 100dvh acompanha a barra do navegador no celular; sem isso o
        // cabecalho e o rodape ficam fora da area visivel no iPhone.
        'fixed inset-0 z-50 flex h-[100dvh] justify-center',
        isFull ? 'items-stretch' : 'items-end sm:items-center',
      )}
    >
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={isPixelWidth ? { width: size, maxWidth: 'calc(100vw - 2rem)' } : undefined}
        className={cn(
          'relative flex w-full flex-col overflow-hidden border border-border bg-background shadow-lg',
          isFull ? '' : 'max-h-[90dvh] rounded-t-lg sm:rounded-lg',
          !isPixelWidth && NAMED_WIDTH[size],
          className,
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between gap-4',
            // Faixa curta e, no celular, sem fundo nem risco proprios: o
            // titulo grande ja diz onde a modal comeca, e cada linha gasta
            // aqui e uma linha a menos de formulario na tela.
            isFull && !compactHeader
              ? 'px-4 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] sm:border-b sm:border-border sm:bg-muted/60 sm:px-6 sm:pb-3'
              : 'border-b border-border px-4 py-2.5 sm:px-6 sm:py-4',
          )}
        >
          <div className="min-w-0">
            <h2 className={cn('font-semibold', isFull && !compactHeader && 'text-xl')}>{title}</h2>
            {subtitle ? (
              <p className={cn('text-muted-foreground', isFull ? 'text-base' : 'text-sm')}>
                {subtitle}
              </p>
            ) : null}
          </div>

          {/* Botao proprio no modo cheio: o `Button` fixa o tamanho do icone. */}
          {isFull && !compactHeader ? (
            <button
              type="button"
              aria-label="Fechar"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:h-11 sm:w-11"
              onClick={onClose}
            >
              <X className="h-6 w-6 sm:h-7 sm:w-7" />
            </button>
          ) : (
            <Button variant="ghost" size="icon" aria-label="Fechar" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* O corpo e branco: a janela e uma folha, e quem separa as acoes do
            conteudo e o cinza do rodape. Telas com cartoes proprios por
            dentro pedem o fundo cinza pelo `bodyClassName`. */}
        <div
          className={cn('flex-1 overflow-y-auto', flushBody ? 'p-0' : 'p-4 sm:p-6', bodyClassName)}
        >
          {children}
        </div>

        {actions.length > 0 ? (
          <div
            className={cn(
              // Barra de acoes: o fundo proprio e o que a separa do conteudo -
              // sem ele os botoes ficam soltos no fim da pagina.
              'flex flex-wrap justify-end gap-2 bg-slate-100 dark:bg-slate-800',
              // A barra de gestos do iPhone cobre o fim da tela: o recuo de
              // baixo respeita a area segura, senao o botao fica sob ela.
              'pb-[max(0.625rem,env(safe-area-inset-bottom))]',
              isFull ? 'px-4 pt-2.5 sm:px-6 sm:pt-4' : 'px-4 pt-2.5 sm:px-6 sm:pt-4 sm:pb-4',
            )}
          >
            {actions.map((action) => (
              <Button
                key={action.label}
                type={action.type ?? 'button'}
                form={action.formId}
                size="sm"
                // No celular as acoes dividem a linha para virarem alvos
                // largos; no desktop voltam ao tamanho do proprio texto.
                className={cn(
                  // No desktop o botao ainda e maior que no celular, mas sem
                  // exagero: 45px em vez de 60 devolve altura ao conteudo.
                  'h-10 flex-1 sm:h-[2.8125rem] sm:flex-none sm:px-6 sm:text-base',
                  // Na modal cheia - o agendamento - ha espaco de sobra: ali
                  // ele fica um degrau maior e vira o alvo obvio da tela.
                  isFull && 'sm:h-13 sm:px-8',
                )}
                variant={action.variant}
                disabled={action.disabled || action.loading}
                onClick={action.onClick}
              >
                {action.loading ? <Spinner /> : action.icon}
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
